// src/pages/conversaciones/components/ChatMessages.tsx
import { useRef, useEffect } from 'react';
import { Avatar, Spinner } from '@/components/ui';
import { Lock } from 'lucide-react';
import { formatDateTime, cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';
import type { Mensaje } from '@/types';

interface Props {
  mensajes: Mensaje[];
  isLoading: boolean;
}

export function ChatMessages({ mensajes, isLoading }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();

  // Scroll al ultimo mensaje
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [mensajes]);

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
      {mensajes.map((mensaje) => {
        const esPropio = mensaje.usuario_id === user?.id;
        const esInterno = mensaje.es_interno;

        return (
          <div
            key={mensaje.id}
            className={cn('flex gap-3', esPropio ? 'flex-row-reverse' : 'flex-row')}
          >
            <Avatar
              fallback={mensaje.usuario?.nombre}
              size="sm"
              className={cn(esInterno && 'ring-2 ring-orange-300')}
            />
            <div
              className={cn(
                'max-w-[70%] rounded-lg p-3',
                esPropio
                  ? 'bg-blue-600 text-white'
                  : esInterno
                    ? 'bg-orange-100 border border-orange-300'
                    : 'bg-white border'
              )}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={cn(
                    'text-xs font-medium',
                    esPropio ? 'text-blue-100' : 'text-gray-500'
                  )}
                >
                  {mensaje.usuario?.nombre}
                </span>
                {esInterno && (
                  <span className="flex items-center gap-1 text-xs text-orange-600">
                    <Lock className="h-3 w-3" />
                    Nota interna
                  </span>
                )}
              </div>
              <p
                className={cn(
                  'text-sm whitespace-pre-wrap',
                  esPropio ? 'text-white' : 'text-gray-800'
                )}
              >
                {mensaje.contenido}
              </p>
              <span
                className={cn('text-xs mt-1 block', esPropio ? 'text-blue-200' : 'text-gray-400')}
              >
                {formatDateTime(mensaje.created_at)}
              </span>
            </div>
          </div>
        );
      })}

      {mensajes.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          No hay mensajes aun. Inicie la conversacion.
        </div>
      )}
    </div>
  );
}
